import { useState, useEffect } from 'react';
import { X, MapPin } from 'lucide-react';
import { inventoryApi, type LocationCreateRequest, type LocationResponse } from '../../api/inventoryApi';

interface LocationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    salonId: string;
    location?: LocationResponse | null; // For editing
}

const locationPresets = [
    { name: 'Salón Principal', icon: '🏪', description: 'Tu ubicación principal de trabajo' },
    { name: 'Bodega Casa', icon: '🏠', description: 'Almacén en tu hogar' },
    { name: 'Segundo Local', icon: '🏢', description: 'Otra sucursal o local' },
    { name: 'Almacén', icon: '📦', description: 'Bodega de almacenamiento' },
];

const LocationModal = ({ isOpen, onClose, onSuccess, salonId, location }: LocationModalProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Form state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [address, setAddress] = useState('');
    const [isDefault, setIsDefault] = useState(false);
    const [selectedPreset, setSelectedPreset] = useState<number | null>(null);

    const isEditing = !!location;

    // Pre-fill form when editing
    useEffect(() => {
        if (location) {
            setName(location.name);
            setDescription(location.description || '');
            setAddress(location.address || '');
            setIsDefault(location.isDefault);
            setSelectedPreset(null);
        } else {
            setName('');
            setDescription('');
            setAddress('');
            setIsDefault(false);
            setSelectedPreset(null);
        }
    }, [location]);

    const handlePresetSelect = (index: number) => {
        const preset = locationPresets[index];
        setSelectedPreset(index);
        setName(preset.name);
        setDescription(preset.description);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            const request: LocationCreateRequest = {
                name,
                description: description || undefined,
                address: address || undefined,
                isDefault,
            };

            if (isEditing && location) {
                await inventoryApi.updateLocation(location.id, request);
            } else {
                await inventoryApi.createLocation(salonId, request);
            }

            onSuccess();
            onClose();
        } catch (err: any) {
            console.error('Error saving location:', err);
            setError(err.response?.data?.message || 'Error al guardar la ubicación');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                            <MapPin className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-white">
                                {isEditing ? 'Editar Ubicación' : 'Nueva Ubicación'}
                            </h2>
                            <p className="text-purple-100 text-sm">
                                {isEditing ? 'Actualiza la información' : 'Agrega un lugar de almacenamiento'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-white" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Preset Buttons (only for new locations) */}
                    {!isEditing && (
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Elige un tipo o crea uno personalizado
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                {locationPresets.map((preset, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => handlePresetSelect(index)}
                                        className={`p-3 rounded-xl border-2 text-left transition-all ${selectedPreset === index
                                            ? 'border-purple-500 bg-purple-50'
                                            : 'border-gray-200 hover:border-purple-300'
                                            }`}
                                    >
                                        <span className="text-xl">{preset.icon}</span>
                                        <p className="font-medium text-gray-900 text-sm mt-1">{preset.name}</p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="space-y-4">
                        {/* Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Nombre de la Ubicación *
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => { setName(e.target.value); setSelectedPreset(null); }}
                                placeholder="Ej: Salón Principal, Bodega Casa"
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                required
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Descripción
                            </label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Descripción opcional..."
                                rows={2}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                            />
                        </div>

                        {/* Address */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Dirección (opcional)
                            </label>
                            <input
                                type="text"
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                placeholder="Ej: Calle 100 #15-20"
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                        </div>

                        {/* Is Default Toggle */}
                        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-100">
                            <div>
                                <p className="font-medium text-gray-900">Ubicación Principal</p>
                                <p className="text-sm text-gray-500">Se usará por defecto al agregar stock</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsDefault(!isDefault)}
                                className={`relative w-12 h-6 rounded-full transition-colors ${isDefault ? 'bg-purple-600' : 'bg-gray-300'
                                    }`}
                            >
                                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${isDefault ? 'left-7' : 'left-1'
                                    }`}></div>
                            </button>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            disabled={isLoading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all disabled:opacity-50 flex items-center gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <MapPin className="w-4 h-4" />
                                    {isEditing ? 'Actualizar' : 'Crear Ubicación'}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LocationModal;
